import { all, call, fork, put, take } from "redux-saga/effects";
import { ClientBookmark } from "src/domain/model/client_bookmark";
import { Customer } from "src/domain/model/customer";
import { IClientBookmarkRepository } from "src/domain/repository/client_bookmark_repository";
import { ICustomerRepository } from "src/domain/repository/customer_repository";
import {
  IJobCareerRepository,
  TJobCareerWithCustomerID,
} from "src/domain/repository/job_career_repository";
import { IApiClient, createApiClient } from "src/infra/api/client";
import { createClientBookmarkAPI } from "src/infra/api/service/client_bookmark_api";
import { createCustomerAPI } from "src/infra/api/service/customer_api";
import { createJobCareerAPI } from "src/infra/api/service/job_career_api";
import {
  ClientIpoCampaignActionType,
  IClientIpoCampaignActionCreator,
  IRequestGetCustomerByClientIpoCampaignPageAction,
  createClientIpoCampaignActionCreator,
} from "../action/client_ipo_campaign";
import { GetCustomersByClientIpoCampaignPageResponse } from "../response/customer_response";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const jobcareerRepository: IJobCareerRepository = createJobCareerAPI(apiClient);
const clientBookmarkRepository: IClientBookmarkRepository =
  createClientBookmarkAPI(apiClient);
const actionCreater: IClientIpoCampaignActionCreator =
  createClientIpoCampaignActionCreator();

function* handleGetCustomersInIpoCampaignPage() {
  while (true) {
    try {
      const action: IRequestGetCustomerByClientIpoCampaignPageAction =
        yield take(ClientIpoCampaignActionType.REQUEST_GET_CUSTOMERS);

      const [
        ipoPrepCustomers,
        internalControlCustomers,
        auditCustomers,
        securitiesAuditCustomers,
        fundraisingCustomers,
        irStrategyCustomers,
        investorRelationsCustomers,
        materialsCustomers,
        globalCustomers,
        cfoCustomers,
        clientBookmarks,
      ] = yield all([
        call(getCustomersByIDs, action.item.ipoPrepCustomerIDs),
        call(getCustomersByIDs, action.item.internalControlCustomerIDs),
        call(getCustomersByIDs, action.item.auditCustomerIDs),
        call(getCustomersByIDs, action.item.securitiesAuditCustomerIDs),
        call(getCustomersByIDs, action.item.fundraisingCustomerIDs),
        call(getCustomersByIDs, action.item.irStrategyCustomerIDs),
        call(getCustomersByIDs, action.item.investorRelationsCustomerIDs),
        call(getCustomersByIDs, action.item.materialsCustomerIDs),
        call(getCustomersByIDs, action.item.globalCustomerIDs),
        call(getCustomersByIDs, action.item.cfoCustomerIDs),
        call(getAllClientBookmark),
      ]);

      let allCustomerList: Customer[] = [];
      allCustomerList = allCustomerList.concat(
        ipoPrepCustomers,
        internalControlCustomers,
        auditCustomers,
        securitiesAuditCustomers,
        fundraisingCustomers,
        irStrategyCustomers,
        investorRelationsCustomers,
        materialsCustomers,
        globalCustomers,
        cfoCustomers
      );
      const customerIdList: string[] = yield call(
        createCustomerIdList,
        allCustomerList
      );
      const jobCareersWithCustomerID: TJobCareerWithCustomerID[] = yield call(
        getJobcareerAllByCustomerIDs,
        customerIdList
      );
      const res: GetCustomersByClientIpoCampaignPageResponse =
        new GetCustomersByClientIpoCampaignPageResponse();
      res.ipoPrepCustomers = ipoPrepCustomers;
      res.internalControlCustomers = internalControlCustomers;
      res.auditCustomers = auditCustomers;
      res.securitiesAuditCustomers = securitiesAuditCustomers;
      res.fundraisingCustomers = fundraisingCustomers;
      res.irStrategyCustomers = irStrategyCustomers;
      res.investorRelationsCustomers = investorRelationsCustomers;
      res.materialsCustomers = materialsCustomers;
      res.globalCustomers = globalCustomers;
      res.cfoCustomers = cfoCustomers;
      res.jobCareersWithCustomerID = jobCareersWithCustomerID;
      res.clientBookmarks = clientBookmarks;

      yield put(
        actionCreater.callbackGetCustomerByClientIpoCampaignPageAction(true, res)
      );
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreater.callbackGetCustomerByClientIpoCampaignPageAction(
          false,
          undefined
        )
      );
    }
  }
}

const createCustomerIdList = (customerList: Customer[]): string[] => {
  return customerList.map((item: Customer) => {
    return item.id;
  });
};

// 指定したID順・件数どおりにCustomerを組み立てる。
// getAllByIDsはサーバ側でID重複が解決されるため、開発環境のように同一IDを
// 複数並べたケースでも、リクエスト件数分のカードを表示できるようにする。
const getCustomersByIDs = (ids: string[]): Promise<Customer[]> => {
  return customerRepository.getAllByIDs(ids).then((customers: Customer[]) => {
    return ids
      .map((id: string) => customers.find((c: Customer) => c.id === id))
      .filter((c: Customer | undefined): c is Customer => c !== undefined);
  });
};

const getJobcareerAllByCustomerIDs = (
  ids: string[]
): Promise<TJobCareerWithCustomerID[]> => {
  return jobcareerRepository.getAllByCustomerIDsForScout(ids);
};

const getAllClientBookmark = (): Promise<ClientBookmark[]> => {
  return clientBookmarkRepository.getAllByClient();
};

export { handleGetCustomersInIpoCampaignPage };



// WEBPACK FOOTER //
// ./src/presentation/handler/client_ipo_campaign_handler.ts